import { useState, useEffect, useCallback } from 'react'
import { History, Download, Calendar, ChevronDown, Search, AlertTriangle, X, RefreshCw, Loader2 } from 'lucide-react'
import PriorityBadge from '@/components/rr/PriorityBadge'
import BucketBadge from '@/components/rr/BucketBadge'
import { rrGetHistory } from '@/actions/rr'

type Priority = 'Very High' | 'High' | 'Medium' | 'Low'
type Bucket = 'ABC' | 'Reorder' | 'RTO'
type AssignType = 'Auto' | 'Manual' | 'Sticky'

interface CapSnapshot { daily: { used: number | null; limit: number | null }; new_lead: { used: number | null; limit: number | null }; not_connected: { used: number | null; limit: number | null } }

interface LogRow {
  id: number; uuid: string
  lead: string; phone: string
  agent: string; agent_id: number
  product: string
  bucket: Bucket; bucket_raw: string
  priority: Priority; priority_raw: string
  timestamp: string
  type: AssignType; type_raw: string
  was_sticky: boolean
  sequence_position: number | null
  override_by: string | null
  cap_snapshot: CapSnapshot
}

const AV_COLORS = ['#2f9e6b','#3b82c4','#b9772e','#8a5cd1','#c2557a','#2aa1a8']
function avColor(n: string) { let h = 0; for (const c of n) h = (h * 31 + c.charCodeAt(0)) % AV_COLORS.length; return AV_COLORS[h] }
function initials(n: string) { const p = n.trim().split(' '); return ((p[0]?.[0]??'')+(p[1]?.[0]??'')).toUpperCase() }

function TypePill({ type }: { type: AssignType }) {
  if (type === 'Auto')   return <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Auto</span>
  if (type === 'Manual') return <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1">Manual override <AlertTriangle className="w-3 h-3" /></span>
  return <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />Sticky</span>
}

function TraceDrawer({ row, onClose }: { row: LogRow; onClose: () => void }) {
  const c = row.cap_snapshot
  const steps = [
    { tone: 'gray' as const, title: 'Lead entered the queue', detail: `Priority computed <b>${row.priority}</b> · bucket: ${row.bucket}` },
    { tone: row.was_sticky ? 'green' as const : 'gray' as const, title: row.was_sticky ? 'Sticky match found' : 'Sticky check — no prior owner', detail: row.was_sticky ? 'Returned to the same agent from a prior interaction.' : 'No earlier agent for this customer; followed normal rotation.' },
    { tone: 'green' as const, title: `Assigned via ${row.type_raw} routing`, detail: row.sequence_position ? `Sequence position ${row.sequence_position}` : 'Manual pick by admin' },
    {
      tone: 'green' as const, title: 'Cap check at time of assignment',
      detail: [
        c.daily.limit     !== null ? `Daily ${c.daily.used}/${c.daily.limit}`             : null,
        c.new_lead.limit  !== null ? `New Lead ${c.new_lead.used}/${c.new_lead.limit}`    : null,
        c.not_connected.limit !== null ? `NC ${c.not_connected.used}/${c.not_connected.limit}` : null,
      ].filter(Boolean).join(' · ') || 'No cap snapshot'
    },
    { tone: 'green' as const, title: `Assigned to ${row.agent}`, detail: `Timestamp: ${row.timestamp}${row.override_by ? ` · overridden by ${row.override_by}` : ''}` },
  ]

  const toneClasses = {
    vh:    'bg-red-50 text-red-500',
    green: 'bg-green-50 text-brandColor',
    gray:  'bg-gray-100 text-gray-400',
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-sm h-full overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-extrabold text-gray-900">Decision Trace</h3>
            <p className="text-xs text-gray-500 mt-0.5">{row.lead} · {row.timestamp}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {steps.map((step, i) => (
            <div key={i} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-extrabold flex-shrink-0 ${toneClasses[step.tone]}`}>{i + 1}</div>
                {i < steps.length - 1 && <div className="w-px flex-1 min-h-[16px] mt-1 bg-gray-100" />}
              </div>
              <div className="flex-1 pb-3">
                <div className="text-sm font-bold text-gray-900">{step.title}</div>
                <div className="text-xs text-gray-500 mt-0.5 leading-relaxed" dangerouslySetInnerHTML={{ __html: step.detail }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const BUCKET_API: Record<string, string> = { ABC: 'abandoned_cart', Reorder: 'reorder', RTO: 'rto' }
const TYPE_API: Record<string, string> = { Auto: 'auto', Manual: 'manual', Sticky: 'sticky' }

export default function HistoryPage() {
  const [log, setLog] = useState<LogRow[]>([])
  const [trace, setTrace] = useState<LogRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [bucketFilter, setBucketFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    rrGetHistory({
      bucket: bucketFilter ? BUCKET_API[bucketFilter] : undefined,
      assignment_type: typeFilter ? TYPE_API[typeFilter] : undefined,
      search: search || undefined,
      page, limit: 50,
    }).then(res => {
      setLog((res?.data ?? []) as LogRow[])
      setTotal(res?.total ?? 0)
    }).finally(() => setLoading(false))
  }, [bucketFilter, typeFilter, search, page])

  useEffect(() => { load() }, [load])

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brandColor flex items-center justify-center flex-shrink-0">
            <History className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">Assignment History</h1>
            <p className="text-sm text-gray-500 mt-0.5">{total} total assignments · click any row to see the decision trace.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 h-9 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button className="inline-flex items-center gap-2 h-9 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} placeholder="Search lead, agent…" className="w-full pl-9 pr-3 h-9 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brandColor/30 focus:border-brandColor" />
        </div>
        <div className="relative">
          <select value={bucketFilter} onChange={e => { setBucketFilter(e.target.value); setPage(1) }} className="appearance-none h-9 pl-3 pr-7 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brandColor/30 bg-white">
            <option value="">All buckets</option>
            <option>ABC</option><option>Reorder</option><option>RTO</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1) }} className="appearance-none h-9 pl-3 pr-7 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brandColor/30 bg-white">
            <option value="">All types</option>
            <option>Auto</option><option>Manual</option><option>Sticky</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        </div>
        <button className="inline-flex items-center gap-2 h-9 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          <Calendar className="w-4 h-4" /> Date range
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex-1">
        <div className="grid grid-cols-[1fr_130px_140px_100px_120px_130px] gap-0 px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100 bg-gray-50">
          <span>Lead</span><span>Agent</span><span>Product</span><span>Bucket</span><span>Priority</span><span>Type · Time</span>
        </div>
        {loading
          ? <div className="flex justify-center items-center h-48"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
          : log.length === 0
            ? <div className="flex justify-center items-center h-32 text-gray-400 text-sm">No assignments found</div>
            : <div className="divide-y divide-gray-50">
              {log.map(row => (
                <div key={row.id} className="grid grid-cols-[1fr_130px_140px_100px_120px_130px] gap-0 px-5 py-3 items-center hover:bg-gray-50/60 cursor-pointer" onClick={() => setTrace(row)}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: avColor(row.lead) }}>{initials(row.lead)}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-gray-900 truncate">{row.lead}</div>
                      <div className="text-xs text-gray-400">{row.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: avColor(row.agent) }}>{initials(row.agent)}</span>
                    <span className="text-xs font-semibold text-gray-700 truncate">{row.agent}</span>
                  </div>
                  <div className="text-xs text-gray-600 truncate pr-2">{row.product || '—'}</div>
                  <div><BucketBadge bucket={row.bucket} /></div>
                  <div><PriorityBadge priority={row.priority} /></div>
                  <div>
                    <TypePill type={row.type} />
                    <div className="text-[10px] text-gray-400 mt-1">{row.timestamp}</div>
                  </div>
                </div>
              ))}
            </div>
        }
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
          <span>Showing {Math.min((page - 1) * 50 + 1, total)}–{Math.min(page * 50, total)} of {total}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-8 px-3 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <button disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)} className="h-8 px-3 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}

      {trace && <TraceDrawer row={trace} onClose={() => setTrace(null)} />}
    </div>
  )
}
